/* prettier-ignore-file */
import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  TextInput,
  Modal,
} from "react-native";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase.config";
import { collection, getDocs, addDoc, deleteDoc, doc, Timestamp } from "firebase/firestore";
import { getCollectionPath } from "../utils/dbPaths";
import AuthHeader from "../components/AuthHeader";
import { useSettings } from "../stores";

const BugReport = () => {
  const navigate = useNavigate();
  const settings = useSettings((state) => state.settings);
  const storeName = settings?.storeName?.name || "";
  const storeLogo = settings?.storeLogo?.url || "";

  const [inputterName, setInputterName] = useState("");
  const [bugDescription, setBugDescription] = useState("");
  const [bugReports, setBugReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Load bug reports on page load
  useEffect(() => {
    loadBugReports();
  }, []);

  const loadBugReports = async () => {
    try {
      setLoading(true);
      const bugReportsPath = getCollectionPath("bugReports");
      const bugReportsRef = collection(db, bugReportsPath);
      const querySnapshot = await getDocs(bugReportsRef);
      
      const reports = [];
      querySnapshot.forEach((doc) => {
        reports.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      
      // Sort by timestamp descending (newest first)
      reports.sort((a, b) => {
        const aTime = a.timestamp?.toDate ? a.timestamp.toDate().getTime() : 0;
        const bTime = b.timestamp?.toDate ? b.timestamp.toDate().getTime() : 0;
        return bTime - aTime;
      });
      
      setBugReports(reports);
    } catch (error) {
      console.error("❌ Error loading bug reports:", error);
      alert("Error loading bug reports: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validate inputs
    if (!inputterName.trim()) {
      alert("Please enter your name");
      return;
    }

    if (!bugDescription.trim()) {
      alert("Please enter a bug description");
      return;
    }

    try {
      setSaving(true);
      
      const bugReportsPath = getCollectionPath("bugReports");
      const bugReportsRef = collection(db, bugReportsPath);
      
      const bugReportData = {
        inputterName: inputterName.trim(),
        description: bugDescription.trim(),
        timestamp: Timestamp.now(),
      };

      await addDoc(bugReportsRef, bugReportData);
      
      // Reload bug reports
      await loadBugReports();
      
      // Clear input fields
      setInputterName("");
      setBugDescription("");
    } catch (error) {
      console.error("❌ Error saving bug report:", error);
      alert("Error saving bug report: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm("Are you sure you want to delete this bug report?")) {
      return;
    }

    try {
      setDeletingId(reportId);
      const bugReportsPath = getCollectionPath("bugReports");
      const reportRef = doc(db, bugReportsPath, reportId);
      await deleteDoc(reportRef);
      
      // Reload bug reports
      await loadBugReports();
    } catch (error) {
      console.error("❌ Error deleting bug report:", error);
      alert("Error deleting bug report: " + error.message);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown date";
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  return (
    <View style={styles.container}>
      <AuthHeader
        title="Bug Report"
        subtitle={storeName ? `by ${storeName}` : ""}
        isDesktop={window.innerWidth >= 768}
        isAuthenticated={true}
        shopInfo={null}
        storeLogo={storeLogo}
      />

      <ScrollView style={styles.scrollView}>
        <View style={styles.content}>
          {/* Bug Report Form */}
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Submit Bug Report</Text>
            
            <View style={styles.inputContainer}>
              <Text style={styles.label}>Inputter Name:</Text>
              <TextInput
                style={styles.input}
                value={inputterName}
                onChangeText={setInputterName}
                placeholder="Enter your name"
                placeholderTextColor="#8A9794"
                editable={!saving}
              />
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>Bug Description:</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={bugDescription}
                onChangeText={setBugDescription}
                placeholder="Describe the bug..."
                placeholderTextColor="#8A9794"
                multiline
                numberOfLines={6}
                textAlignVertical="top"
                editable={!saving}
              />
            </View>

            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.submitButton, saving && styles.submitButtonDisabled]}
              onPress={handleSubmit}
              disabled={saving}
            >
              <Text style={styles.submitButtonText}>Submit</Text>
            </TouchableOpacity>
          </View>

          {/* Previous Bug Reports */}
          <View style={styles.reportsSection}>
            <Text style={styles.sectionTitle}>Previous Bug Reports</Text>
            
            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#0E7C6B" />
                <Text style={styles.loadingText}>Loading bug reports...</Text>
              </View>
            ) : bugReports.length === 0 ? (
              <Text style={styles.noReportsText}>No bug reports yet</Text>
            ) : (
              bugReports.map((report) => (
                <View key={report.id} style={styles.reportCard}>
                  <View style={styles.reportHeader}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.reportInputter}>{report.inputterName}</Text>
                      <Text style={styles.reportDate}>{formatDate(report.timestamp)}</Text>
                    </View>
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={styles.deleteButton}
                      onPress={() => handleDelete(report.id)}
                      disabled={deletingId === report.id}
                    >
                      {deletingId === report.id ? (
                        <ActivityIndicator size="small" color="#C4453D" />
                      ) : (
                        <Text style={styles.deleteButtonText}>🗑️</Text>
                      )}
                    </TouchableOpacity>
                  </View>
                  <Text style={styles.reportDescription}>{report.description}</Text>
                </View>
              ))
            )}
          </View>
        </View>
      </ScrollView>

      {/* Blocking Modal While Saving */}
      <Modal
        visible={saving}
        transparent={true}
        animationType="fade"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.modalText}>Saving bug report...</Text>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F8F7",
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
    maxWidth: 800,
    alignSelf: "center",
    width: "100%",
  },
  formSection: {
    backgroundColor: "#FFFFFF",
    padding: 20,
    borderRadius: 6,
    marginBottom: 20,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#0E7C6B",
    marginBottom: 16,
  },
  inputContainer: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 12,
    fontSize: 16,
    backgroundColor: "#FFFFFF",
  },
  textArea: {
    minHeight: 120,
    paddingTop: 12,
  },
  submitButton: {
    backgroundColor: "#0E7C6B",
    padding: 16,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 8,
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  reportsSection: {
    backgroundColor: "#FFFFFF",
    padding: 20,
    borderRadius: 6,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  loadingContainer: {
    alignItems: "center",
    padding: 40,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: "#5C6B68",
  },
  noReportsText: {
    fontSize: 14,
    color: "#8A9794",
    fontStyle: "italic",
    textAlign: "center",
    padding: 40,
  },
  reportCard: {
    backgroundColor: "#F4F6F6",
    padding: 16,
    borderRadius: 6,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
  },
  reportHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  reportInputter: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
  },
  reportDate: {
    fontSize: 12,
    color: "#5C6B68",
    marginTop: 4,
  },
  deleteButton: {
    padding: 8,
    marginLeft: 12,
  },
  deleteButtonText: {
    fontSize: 20,
  },
  reportDescription: {
    fontSize: 14,
    color: "#5C6B68",
    lineHeight: 20,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "#FFFFFF",
    padding: 30,
    borderRadius: 8,
    alignItems: "center",
    minWidth: 200,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  modalText: {
    marginTop: 16,
    fontSize: 16,
    color: "#2E3A38",
    fontWeight: "500",
  },
});

export default BugReport;

